// @owners { team: patients-team }
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Column,
  Description,
  InlineAlert,
  InputPassword,
  InputSelect,
  InputText,
  LgPadding,
  Row,
  Toast,
  ToastContext,
} from '@alto/design-system';
import React, { useContext, useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { clearUserError, fetchUsers, updateUser } from '~shared/actions/users';
import {
  DEFAULT_ERROR_ALERT_MESSAGE,
  DEFAULT_ERROR_ALERT_TITLE,
  PASSWORD_ERROR_MESSAGE,
  phoneTypes,
} from '~shared/constants';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import getUserError from '~shared/selectors/ui/errors/getUserError';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type User } from '~shared/types/clients';
import { validateEmail, validatePassword, validatePhone } from '~shared/validations';

const PHONE_TYPE_OPTIONS = phoneTypes.map((type) => ({
  label: type,
  value: type.toLowerCase(),
}));

type Values = {
  phone: string;
  phone_type: string;
  secondary_contact_phone: string;
  secondary_phone_type: string;
  email: string;
  password: string;
  password_confirmation: string;
};

function getInitialValues(user: User | undefined) {
  const { phone, phone_type, secondary_contact_phone, secondary_phone_type, email } = user || {};

  return {
    phone: phone || '',
    phone_type: phone_type || '',
    secondary_contact_phone: secondary_contact_phone || '',
    secondary_phone_type: secondary_phone_type || '',
    email: email || '',
    password: '',
    password_confirmation: '',
  };
}

const getFormattedValues = (values: Values) => {
  if (values.phone) {
    values.phone = values.phone.replace(/\D/g, '');
  }

  if (values.secondary_contact_phone) {
    values.secondary_contact_phone = values.secondary_contact_phone.replace(/\D/g, '');
  }

  const formattedValues: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== '') {
      formattedValues[key as keyof Values] = value;
    }
  }

  // clear the secondary_phone_type when no secondary contact phone number
  if (!values.secondary_contact_phone) {
    formattedValues.secondary_contact_phone = '';
    formattedValues.secondary_phone_type = '';
  }

  return formattedValues;
};

export const ContactInfoActionSheet = () => {
  const dispatch = useDispatchShared();
  const { addToast } = useContext(ToastContext);
  const { closeActionSheet } = useContext(ActionSheetContext);
  const user = useSelectorShared(getCurrentUser);
  const error = useSelectorShared(getUserError);
  const loading = useSelectorShared((state) => state.ui.loading.updateUserLoading);

  const { control, handleSubmit, reset } = useForm<ReturnType<typeof getInitialValues>>({
    defaultValues: getInitialValues(user),
  });

  // Reset form when user data changes (e.g., after refetch)
  useEffect(() => {
    reset(getInitialValues(user));
  }, [user, reset]);

  const phoneController = useController({
    control,
    name: 'phone',
    rules: { required: 'Phone number is required', validate: validatePhone },
  });

  const phoneTypeController = useController({
    control,
    name: 'phone_type',
  });

  const secondaryPhoneController = useController({
    control,
    name: 'secondary_contact_phone',
  });

  const secondaryPhoneTypeController = useController({
    control,
    name: 'secondary_phone_type',
  });

  const emailController = useController({
    control,
    name: 'email',
    rules: { required: 'Email is required', validate: validateEmail },
  });

  const passwordController = useController({
    control,
    name: 'password',
    rules: { validate: validatePassword },
  });

  const passwordConfirmationController = useController({
    control,
    name: 'password_confirmation',
    rules: {
      validate: (passwordConfirmation, { password }) => {
        if (password && passwordConfirmation !== password) return 'Passwords must match';
        return true;
      },
    },
  });

  const onSubmit = async (values: Values) => {
    const formattedValues = getFormattedValues(values);
    // eslint-disable-next-line import/no-deprecated
    const { success } = await dispatch(updateUser(formattedValues));

    if (success) {
      dispatch(clearUserError());
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchUsers());
      closeActionSheet();
      addToast(<Toast>Updated contact and login info</Toast>);
    } else {
      addToast(
        <Toast variant="error">
          {DEFAULT_ERROR_ALERT_TITLE}. {DEFAULT_ERROR_ALERT_MESSAGE}
        </Toast>,
      );
    }
  };

  return (
    <ActionSheetV2
      title="Edit phone number and login"
      analyticsName="contact_info"
      buttons={[
        <Button
          label="Save"
          accessibilityLabel="Press to save contact info"
          loading={loading}
          loadingLabel="Saving"
          onPress={handleSubmit(onSubmit)}
          key="save-button"
        />,
        <Button
          label="Cancel"
          accessibilityLabel="Press to close contact info"
          type="tertiary"
          onPress={closeActionSheet}
          key="cancel-button"
        />,
      ]}
    >
      <LgPadding>
        <Column gap="SM">
          <Row gap="LG">
            <InputText
              accessibilityLabel="Phone number"
              required
              label="Phone number"
              placeholder="(555) 555-5555"
              autoCapitalize="none"
              textContentType="telephoneNumber"
              {...mapControllerToInputProps(phoneController)}
            />
            <InputSelect
              label="Phone type"
              onValueChange={(value) => {
                phoneTypeController.field.onChange(value);
              }}
              options={PHONE_TYPE_OPTIONS}
              placeholder="Select phone type"
              modalTitle="Select phone type"
              {...mapControllerToInputProps(phoneTypeController)}
            />
          </Row>
          <Row gap="LG">
            <InputText
              accessibilityLabel="Alternate phone number"
              label="Alternate phone number"
              placeholder="(555) 555-5555"
              autoCapitalize="none"
              textContentType="telephoneNumber"
              {...mapControllerToInputProps(secondaryPhoneController)}
            />
            <InputSelect
              label="Alternate phone type"
              onValueChange={(value) => {
                secondaryPhoneTypeController.field.onChange(value);
              }}
              options={PHONE_TYPE_OPTIONS}
              placeholder="Select alternate phone type"
              modalTitle="Select alternate phone type"
              {...mapControllerToInputProps(secondaryPhoneTypeController)}
            />
          </Row>
          <InputText
            accessibilityLabel="Email"
            required
            label="Email"
            placeholder="username@email.com"
            autoCapitalize="none"
            textContentType="emailAddress"
            {...mapControllerToInputProps(emailController)}
          />
          <InputPassword
            accessibilityLabel="New password"
            label="New password"
            caption={PASSWORD_ERROR_MESSAGE}
            placeholder="•••••••••••••"
            textContentType="newPassword"
            onChangeText={passwordController.field.onChange}
            {...mapControllerToInputProps(passwordController)}
          />
          <InputPassword
            accessibilityLabel="Confirm new password"
            label="Confirm new password"
            placeholder="•••••••••••••"
            textContentType="newPassword"
            onChangeText={passwordConfirmationController.field.onChange}
            {...mapControllerToInputProps(passwordConfirmationController)}
          />
          {error ? (
            <InlineAlert type="error">
              <Description>{getErrorMessage(error)}</Description>
            </InlineAlert>
          ) : null}
        </Column>
      </LgPadding>
    </ActionSheetV2>
  );
};
